import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { orderStatusLabel, paymentStatusLabel } from '../../utils/statusLabel';
import {
  Phone,
  PhoneCall,
  Calendar,
  CreditCard,
  PackageCheck,
  Coins,
  Truck,
  AlertCircle,
  CheckCircle,
  X,
  Inbox,
  User,
  UserPlus,
  MapPin,
  Receipt,
  ShieldCheck,
  Clock,
} from 'lucide-react';
import SalesLayout from '../../components/layout/SalesLayout';
import { hasTask, getStoredUser } from '../../utils/tasks';

// ─── Design tokens ─────────────────────────────────────────────────────────
const tokens = {
  surface: '#ffffff',
  surfaceSoft: '#f1f5f9',
  surfaceTint: '#fafbff',
  ink: '#0f172a',
  body: '#334155',
  muted: '#64748b',
  subtle: '#94a3b8',
  hairline: '#e5e7eb',
  hairlineSoft: '#eef0f4',
  brand: '#6366f1',
  brandDeep: '#4f46e5',
  brandSoft: 'rgba(99, 102, 241, 0.08)',
  brand33: 'rgba(99, 102, 241, 0.32)',
  success: '#10b981',
  successSoft: 'rgba(16, 185, 129, 0.10)',
  successDeep: '#065f46',
  warning: '#f59e0b',
  warningSoft: 'rgba(245, 158, 11, 0.12)',
  warningDeep: '#92400e',
  danger: '#ef4444',
  dangerSoft: 'rgba(239, 68, 68, 0.10)',
  dangerDeep: '#991b1b',
  info: '#06b6d4',
  infoSoft: 'rgba(6, 182, 212, 0.10)',
  shadowSm: '0 1px 2px rgba(15, 23, 42, 0.04)',
  shadowMd: '0 4px 12px rgba(15, 23, 42, 0.06)',
  shadowLg: '0 12px 32px rgba(15, 23, 42, 0.10)',
  radiusSm: '8px',
  radiusMd: '12px',
  radiusLg: '16px',
  radiusXl: '20px',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ─── Layout ────────────────────────────────────────────────────────────────
const PageWrap = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// ─── Header ────────────────────────────────────────────────────────────────
const Header = styled.header`
  background: linear-gradient(135deg, #ffffff 0%, ${tokens.surfaceTint} 100%);
  border: 1px solid ${tokens.hairline};
  border-radius: ${tokens.radiusXl};
  padding: 1.5rem 1.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  box-shadow: ${tokens.shadowSm};
  position: relative;
  overflow: hidden;
  animation: ${fadeUp} 0.35s ease-out both;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(600px 200px at 100% 0%, rgba(99, 102, 241, 0.08), transparent 60%);
    pointer-events: none;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 1.25rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 1;
`;

const Avatar = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${tokens.brand} 0%, ${tokens.brandDeep} 100%);
  color: white;
  font-weight: 700;
  font-size: 1.15rem;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.04em;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.32);
  flex: 0 0 auto;
`;

const Greeting = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const GreetingEyebrow = styled.span`
  font-size: 0.78rem;
  font-weight: 500;
  color: ${tokens.muted};
`;

const TitleEl = styled.h1`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${tokens.ink};
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.15;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
`;

const DatePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  background: ${tokens.surface};
  border: 1px solid ${tokens.hairline};
  font-size: 0.85rem;
  font-weight: 500;
  color: ${tokens.body};
  box-shadow: ${tokens.shadowSm};

  svg { color: ${tokens.muted}; }
`;

// ─── Section card ──────────────────────────────────────────────────────────
const Section = styled.section`
  background: ${tokens.surface};
  border: 1px solid ${tokens.hairline};
  border-radius: ${tokens.radiusXl};
  padding: 1.4rem 1.4rem 1.2rem;
  box-shadow: ${tokens.shadowSm};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  animation: ${fadeUp} 0.4s ease-out both;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: ${tokens.ink};
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;

  svg { color: ${p => p.$accent || tokens.brand}; }
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  padding: 0.1rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 999px;
  background: ${p => p.$bg || tokens.brandSoft};
  color: ${p => p.$fg || tokens.brandDeep};
  font-variant-numeric: tabular-nums;
`;

const SectionHint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${tokens.muted};
`;

// ─── Order rows ────────────────────────────────────────────────────────────
const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const highlightPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.55); border-color: ${tokens.brand}; }
  60%  { box-shadow: 0 0 0 14px rgba(99, 102, 241, 0); border-color: ${tokens.brand}; }
  100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); border-color: ${tokens.hairline}; }
`;

const Row = styled.article`
  position: relative;
  background: ${tokens.surface};
  border: 1px solid ${tokens.hairline};
  border-radius: ${tokens.radiusLg};
  padding: 0.95rem 1.05rem 0.95rem 1.4rem;
  transition: border-color 0.18s, box-shadow 0.18s;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: ${p => p.$stripe || tokens.subtle};
    border-radius: ${tokens.radiusLg} 0 0 ${tokens.radiusLg};
  }

  &:hover {
    border-color: ${tokens.brand33};
    box-shadow: ${tokens.shadowMd};
  }

  ${p => p.$highlight && css`
    border-color: ${tokens.brand};
    background: ${tokens.surfaceTint};
    animation: ${highlightPulse} 2.4s ease-out 1;
  `}
`;

const RowMain = styled.div`
  flex: 1 1 320px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RowTopLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const OrderHash = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${tokens.ink};
  font-variant-numeric: tabular-nums;

  &::before {
    content: '#';
    color: ${tokens.subtle};
    font-weight: 500;
    margin-right: 1px;
  }
`;

const CustomerName = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${tokens.body};
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${p => p.$bg || tokens.surfaceSoft};
  color: ${p => p.$fg || tokens.body};

  svg { width: 11px; height: 11px; }
`;

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem 1.2rem;
  font-size: 0.82rem;
  color: ${tokens.muted};

  > span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  strong {
    font-weight: 600;
    color: ${tokens.body};
    font-variant-numeric: tabular-nums;
  }

  svg { color: ${tokens.subtle}; }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const PhoneLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  background: ${tokens.successSoft};
  color: ${tokens.successDeep};
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid rgba(16, 185, 129, 0.25);
  transition: background 0.15s, transform 0.1s;

  &:hover { background: ${tokens.success}; color: white; }
  &:active { transform: translateY(1px); }
`;

const PhoneEmpty = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  background: ${tokens.surfaceSoft};
  color: ${tokens.subtle};
  font-size: 0.82rem;
  font-weight: 500;
`;

const Btn = styled.button`
  appearance: none;
  font: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.82rem;
  font-weight: 600;
  transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.1s;

  &.primary {
    background: ${tokens.brand};
    color: white;
    box-shadow: 0 1px 2px rgba(99, 102, 241, 0.4);

    &:hover { background: ${tokens.brandDeep}; }
    &:active { transform: translateY(1px); }
  }

  &.success {
    background: ${tokens.success};
    color: white;

    &:hover { background: #059669; }
    &:active { transform: translateY(1px); }
  }

  &.warning {
    background: ${tokens.warning};
    color: white;

    &:hover { background: #d97706; }
  }

  &.secondary {
    background: ${tokens.surface};
    color: ${tokens.body};
    border-color: ${tokens.hairline};

    &:hover { border-color: ${tokens.brand33}; color: ${tokens.ink}; }
  }

  &.danger {
    background: ${tokens.danger};
    color: white;

    &:hover { background: #dc2626; }
  }

  &:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
  &:focus-visible { outline: 2px solid ${tokens.brand}; outline-offset: 2px; }
`;

// ─── Empty + skeleton ──────────────────────────────────────────────────────
const Empty = styled.div`
  text-align: center;
  padding: 2.25rem 1.25rem;
  border: 1.5px dashed ${tokens.hairline};
  border-radius: ${tokens.radiusLg};
  background: ${tokens.surfaceTint};
  color: ${tokens.muted};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  .icon-wrap {
    width: 50px;
    height: 50px;
    border-radius: 14px;
    background: ${tokens.brandSoft};
    color: ${tokens.brand};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  h4 {
    margin: 0.3rem 0 0;
    font-size: 0.95rem;
    color: ${tokens.ink};
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
  }
`;

const SkeletonBlock = styled.div`
  height: ${p => p.$h || '14px'};
  width: ${p => p.$w || '100%'};
  border-radius: 6px;
  background: linear-gradient(90deg, ${tokens.surfaceSoft} 0%, ${tokens.hairlineSoft} 50%, ${tokens.surfaceSoft} 100%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite linear;
`;

const SkeletonRow = () => (
  <Row $stripe={tokens.subtle}>
    <RowMain>
      <RowTopLine>
        <SkeletonBlock $w="80px" $h="14px" />
        <SkeletonBlock $w="160px" $h="14px" />
      </RowTopLine>
      <SkeletonBlock $w="60%" $h="11px" />
    </RowMain>
  </Row>
);

// ─── Modals ────────────────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 0.15s ease-out;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: ${tokens.surface};
  padding: 1.85rem 1.85rem 1.5rem;
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 540px;
  border: 1px solid ${tokens.hairline};
  box-shadow: ${tokens.shadowLg};
  animation: ${fadeUp} 0.25s ease-out;
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const ModalTitleEl = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${p => p.$tone === 'danger' ? tokens.dangerDeep : tokens.ink};
  display: flex;
  align-items: center;
  gap: 0.6rem;

  .icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${p => p.$tone === 'danger' ? tokens.dangerSoft
      : p.$tone === 'success' ? tokens.successSoft
      : p.$tone === 'warning' ? tokens.warningSoft
      : tokens.brandSoft};
    color: ${p => p.$tone === 'danger' ? tokens.danger
      : p.$tone === 'success' ? tokens.success
      : p.$tone === 'warning' ? tokens.warning
      : tokens.brand};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const ModalCloseBtn = styled.button`
  appearance: none;
  background: ${tokens.surfaceSoft};
  border: 1px solid ${tokens.hairline};
  border-radius: 10px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${tokens.muted};

  &:hover { background: ${tokens.danger}; color: white; border-color: ${tokens.danger}; }
`;

const ModalText = styled.p`
  color: ${tokens.body};
  line-height: 1.55;
  margin: 0 0 1rem;
  font-size: 0.92rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

const FormLabel = styled.label`
  font-size: 0.82rem;
  font-weight: 600;
  color: ${tokens.body};
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;

  svg { color: ${tokens.muted}; }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.7rem 0.95rem;
  border: 1px solid ${tokens.hairline};
  border-radius: ${tokens.radiusMd};
  background: ${tokens.surfaceTint};
  font-size: 0.92rem;
  color: ${tokens.ink};
  box-sizing: border-box;

  &:focus { outline: none; border-color: ${tokens.brand}; background: ${tokens.surface}; box-shadow: 0 0 0 3px ${tokens.brandSoft}; }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.7rem 0.95rem;
  border: 1px solid ${tokens.hairline};
  border-radius: ${tokens.radiusMd};
  background: ${tokens.surfaceTint};
  font-size: 0.92rem;
  color: ${tokens.ink};
  box-sizing: border-box;
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.7rem 0.95rem;
  border: 1px solid ${tokens.hairline};
  border-radius: ${tokens.radiusMd};
  background: ${tokens.surfaceTint};
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;

  &:focus { outline: none; border-color: ${tokens.brand}; background: ${tokens.surface}; box-shadow: 0 0 0 3px ${tokens.brandSoft}; }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.7rem;
  margin-top: 0.5rem;

  > button { flex: 1; }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
  margin: 0.4rem 0 1rem;
  padding: 0.85rem 1rem;
  background: ${tokens.surfaceTint};
  border: 1px solid ${tokens.hairlineSoft};
  border-radius: ${tokens.radiusMd};
  font-size: 0.88rem;

  > div {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;

    .label { color: ${tokens.muted}; }
    .value { font-weight: 700; color: ${tokens.ink}; font-variant-numeric: tabular-nums; }
  }
`;

const WaiverBlock = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  border: 1px solid ${tokens.warning};
  background: ${tokens.warningSoft};
  border-radius: ${tokens.radiusMd};
  margin-bottom: 1rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: ${tokens.warningDeep};
  line-height: 1.4;

  input { margin-top: 0.2rem; }
  strong { font-weight: 700; }
`;

const SuccessBanner = styled.div`
  background: ${tokens.successSoft};
  border: 1px solid #bbf7d0;
  color: ${tokens.successDeep};
  padding: 0.65rem 0.9rem;
  border-radius: ${tokens.radiusMd};
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
  animation: ${fadeUp} 0.2s ease-out;
`;

const InlineError = styled.div`
  background: ${tokens.dangerSoft};
  border: 1px solid #fecaca;
  color: ${tokens.dangerDeep};
  padding: 0.55rem 0.8rem;
  border-radius: 8px;
  font-size: 0.82rem;
  margin-bottom: 0.7rem;
`;

// ─── Helpers ───────────────────────────────────────────────────────────────
const greetingKeyFor = (date = new Date()) => {
  const h = date.getHours();
  if (h < 12) return 'salesOverview.goodMorning';
  if (h < 18) return 'salesOverview.goodAfternoon';
  return 'salesOverview.goodEvening';
};

const initialsFor = (name) => {
  if (!name) return 'SP';
  const parts = String(name).trim().split(/\s+/);
  return parts.slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('') || 'SP';
};

const fullName = (visitor, order = null) => {
  if (!visitor && !order) return 'Unknown customer';
  const first = visitor?.firstName || '';
  const last = visitor?.lastName || '';
  const composed = `${first} ${last}`.trim();
  if (composed) return composed;
  // Fall back to prescription's patientName via the order
  const prescriptionName = order?.prescription?.patientName || order?.raw_data?.patientName;
  if (prescriptionName) return prescriptionName;
  if (visitor?.gender) return `${visitor.gender} — ${visitor.ageGroup || '—'}`;
  return 'Unknown customer';
};

const fmtMoney = (n) => `${(Number(n) || 0).toLocaleString()} DZD`;

const balanceOf = (order) => {
  const total = Number(order?.total) || 0;
  const paid = Number(order?.payment?.amount) || 0;
  return Math.max(0, total - paid);
};

const paidOf = (order) => Number(order?.payment?.amount) || 0;

const minimumPaidForDelivery = (order) => {
  const total = Number(order?.total) || 0;
  return total > 0 ? total * 0.5 : 0;
};

const requiresDeliveryWaiver = (order) => paidOf(order) < minimumPaidForDelivery(order);

const daysSince = (date) => {
  if (!date) return null;
  const ms = Date.now() - new Date(date).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const paymentPill = (order) => {
  const status = (order?.payment?.status || 'pending').toLowerCase();
  const label = paymentStatusLabel(status);
  if (status === 'paid')   return { label, bg: tokens.successSoft, fg: tokens.successDeep };
  if (status === 'partial')return { label, bg: tokens.warningSoft, fg: tokens.warningDeep };
  if (status === 'refunded')return{ label, bg: tokens.dangerSoft,  fg: tokens.dangerDeep };
  return { label, bg: tokens.dangerSoft, fg: tokens.dangerDeep };
};

const lifecyclePill = (order) => {
  const s = order?.status || 'pending';
  const label = orderStatusLabel(s);
  if (s === 'pending')              return { label, bg: tokens.warningSoft, fg: tokens.warningDeep };
  if (s === 'confirmed')            return { label, bg: tokens.brandSoft,   fg: tokens.brandDeep };
  if (s === 'ready_for_pickup')     return { label, bg: tokens.successSoft, fg: tokens.successDeep };
  if (s === 'delivered')            return { label, bg: tokens.successSoft, fg: tokens.successDeep };
  if (s === 'completed')            return { label, bg: tokens.successSoft, fg: tokens.successDeep };
  if (s === 'cancelled')            return { label, bg: tokens.dangerSoft,  fg: tokens.dangerDeep };
  return { label, bg: tokens.surfaceSoft, fg: tokens.body };
};

const stripeFor = (order) => lifecyclePill(order).fg;

// ─── Component ─────────────────────────────────────────────────────────────
const SalesOverview = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [todaysOrders, setTodaysOrders] = useState([]);
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  const [pendingDelivery, setPendingDelivery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  // Notification deep-link target. When ?highlight=ORD-123 is present
  // in the URL we pulse and scroll to the matching row across the
  // three sections, so the user can pick up the conversation right
  // where the notification took them.
  const highlightOrder = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('highlight') || null;
  }, [location.search]);
  const [highlightActive, setHighlightActive] = useState(false);
  const highlightTimerRef = useRef(null);

  // Mandatory-reason popup (legacy: open orders that need a reason logged).
  const [openOrders, setOpenOrders] = useState([]);
  const [showReasonPopup, setShowReasonPopup] = useState(false);
  const [currentReasonOrder, setCurrentReasonOrder] = useState(null);
  const [reasonText, setReasonText] = useState('');
  const [submittingReason, setSubmittingReason] = useState(false);

  // Doctor modal.
  const [showDoctorModal, setShowDoctorModal] = useState(false);
  const [doctorForm, setDoctorForm] = useState({ name: '', phoneNumber: '', address: '', city: '', country: '' });
  const [doctorSaving, setDoctorSaving] = useState(false);
  const [doctorSuccess, setDoctorSuccess] = useState('');

  // Payment modal.
  const [paymentModal, setPaymentModal] = useState({ open: false, order: null });
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'cash', notes: '' });
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Delivery modal.
  const [deliverModal, setDeliverModal] = useState({ open: false, order: null });
  const [deliverForm, setDeliverForm] = useState({ waiverSigned: false, notes: '' });
  const [deliverSaving, setDeliverSaving] = useState(false);
  const [deliverError, setDeliverError] = useState('');

  // Task switches: recording the rest to pay is the cashier's job ("See
  // payments and deposits"); delivering has its own switch. The server
  // enforces both — this just hides buttons that would be refused.
  const canCollect = hasTask(getStoredUser(), 'cashier.payments');
  const canDeliver = hasTask(getStoredUser(), 'sales.deliver');

  // Use the logged-in user object (same source as the sidebar) so the greeting
  // never falls out of sync with the stale `user_name` key.
  const userName = (() => {
    let u = {};
    try { u = JSON.parse(localStorage.getItem('user') || '{}'); } catch (_) { u = {}; }
    return u.fullName
      || [u.firstName, u.lastName].filter(Boolean).join(' ')
      || localStorage.getItem('user_name')
      || 'Sales Person';
  })();
  const dateLocale = { en: 'en-US', fr: 'fr-FR', ar: 'ar-EG' }[i18n.language] || 'en-US';
  const todayLabel = new Date().toLocaleDateString(dateLocale, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const fetchOverview = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/orders/sales/overview', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTodaysOrders(data.todaysOrders || []);
      setUnpaidOrders(data.unpaidOrders || []);
      setPendingDelivery(data.pendingDelivery || []);
    } catch (err) {
      console.error('Sales overview fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOpenOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/orders/sales/open-orders', {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) return;
      const data = await response.json();
      if (!data.openOrders?.length) return;

      const needsReason = data.openOrders.filter(order => {
        if (!order.latestReason) return true;
        const lastReasonDate = new Date(order.latestReason.createdAt);
        const oneDay = 24 * 60 * 60 * 1000;
        return (Date.now() - lastReasonDate.getTime()) > oneDay;
      });

      if (needsReason.length > 0) {
        setOpenOrders(needsReason);
        setCurrentReasonOrder(needsReason[0]);
        setShowReasonPopup(true);
      }
    } catch (error) {
      console.error('Error fetching open orders:', error);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    fetchOpenOrders();
  }, [fetchOverview, fetchOpenOrders, refreshTick]);

  // Once the lists are loaded, scroll to the highlighted row (if any)
  // and let the pulse animation play. We auto-clear after a few
  // seconds so the visual cue doesn't stick around forever.
  useEffect(() => {
    if (!highlightOrder || loading) return;
    setHighlightActive(true);
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-order-ref="${CSS.escape(highlightOrder)}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightActive(false), 4000);
    return () => {
      clearTimeout(t);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, [highlightOrder, loading, todaysOrders, unpaidOrders, pendingDelivery]);

  const isHighlighted = useCallback(
    (order) => highlightActive && highlightOrder &&
      (order?.orderNumber === highlightOrder || String(order?._id) === highlightOrder),
    [highlightActive, highlightOrder]
  );

  // ─── Reason popup ────────────────────────────────────────────────────────
  const handleSubmitReason = async () => {
    if (!reasonText.trim() || !currentReasonOrder) return;
    setSubmittingReason(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/orders/${currentReasonOrder._id}/add-reason`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reasonText }),
      });
      if (res.ok) {
        const remaining = openOrders.filter(o => o._id !== currentReasonOrder._id);
        setOpenOrders(remaining);
        setReasonText('');
        if (remaining.length > 0) {
          setCurrentReasonOrder(remaining[0]);
        } else {
          setShowReasonPopup(false);
          setCurrentReasonOrder(null);
        }
        setRefreshTick(t => t + 1);
      }
    } catch (err) {
      console.error('Submit reason error:', err);
    } finally {
      setSubmittingReason(false);
    }
  };

  // ─── Doctor modal ────────────────────────────────────────────────────────
  const handleSaveDoctor = async () => {
    if (!doctorForm.name.trim()) return;
    setDoctorSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/doctors', doctorForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setDoctorSuccess(`${res.data.doctor.name} added.`);
        setDoctorForm({ name: '', phoneNumber: '', address: '', city: '', country: '' });
        setTimeout(() => {
          setDoctorSuccess('');
          setShowDoctorModal(false);
        }, 1500);
      }
    } catch (err) {
      console.error('Save doctor error:', err);
      alert('Failed to save doctor');
    } finally {
      setDoctorSaving(false);
    }
  };

  // ─── Payment ─────────────────────────────────────────────────────────────
  const openPayment = (order, opts = {}) => {
    setPaymentError('');
    setPaymentForm({
      amount: opts.full ? String(balanceOf(order)) : '',
      method: order?.payment?.method || 'cash',
      notes: '',
    });
    setPaymentModal({ open: true, order });
  };

  const closePayment = () => {
    setPaymentModal({ open: false, order: null });
    setPaymentForm({ amount: '', method: 'cash', notes: '' });
    setPaymentError('');
  };

  const submitPayment = async () => {
    const order = paymentModal.order;
    if (!order) return;
    const amt = Number(paymentForm.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setPaymentError('Enter a positive amount.');
      return;
    }
    setPaymentSaving(true);
    setPaymentError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/orders/${order._id}/payment`, {
        amount: amt,
        method: paymentForm.method,
        notes: paymentForm.notes || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      closePayment();
      setRefreshTick(t => t + 1);
    } catch (err) {
      setPaymentError(err.response?.data?.message || 'Failed to record payment.');
    } finally {
      setPaymentSaving(false);
    }
  };

  // ─── Delivery ────────────────────────────────────────────────────────────
  const openDeliver = (order) => {
    setDeliverError('');
    setDeliverForm({ waiverSigned: false, notes: '' });
    setDeliverModal({ open: true, order });
  };

  const closeDeliver = () => {
    setDeliverModal({ open: false, order: null });
    setDeliverForm({ waiverSigned: false, notes: '' });
    setDeliverError('');
  };

  const submitDeliver = async () => {
    const order = deliverModal.order;
    if (!order) return;
    const needsWaiver = requiresDeliveryWaiver(order);
    if (needsWaiver && !deliverForm.waiverSigned) {
      setDeliverError('Customer must sign the waiver when payment is under 50%.');
      return;
    }
    setDeliverSaving(true);
    setDeliverError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/orders/${order._id}/deliver`, {
        waiverSigned: deliverForm.waiverSigned,
        notes: deliverForm.notes || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      closeDeliver();
      setRefreshTick(t => t + 1);
    } catch (err) {
      setDeliverError(err.response?.data?.message || 'Failed to deliver order.');
    } finally {
      setDeliverSaving(false);
    }
  };

  // ─── Counts (memoized for the section badges) ────────────────────────────
  const counts = useMemo(() => ({
    today: todaysOrders.length,
    unpaid: unpaidOrders.length,
    delivery: pendingDelivery.length,
  }), [todaysOrders, unpaidOrders, pendingDelivery]);

  return (
    <SalesLayout pageTitle={t('salesOverview.overview')}>
      <PageWrap>
        {/* ── Reason popup ────────────────────────────────────────────── */}
        {showReasonPopup && currentReasonOrder && (
          <ModalOverlay>
            <ModalContent>
              <ModalHead>
                <ModalTitleEl $tone="danger">
                  <span className="icon-wrap"><AlertCircle size={20} /></span>
                  {t('salesOverview.unconfirmedOrder')}
                </ModalTitleEl>
              </ModalHead>
              <SummaryGrid>
                <div>
                  <span className="label">{t('salesOverview.visitor')}</span>
                  <span className="value">
                    {currentReasonOrder.visitor?.gender || 'Unknown'} ({currentReasonOrder.visitor?.ageGroup || '—'})
                  </span>
                </div>
                <div>
                  <span className="label">{t('salesOverview.date')}</span>
                  <span className="value">{new Date(currentReasonOrder.createdAt).toLocaleDateString(dateLocale)}</span>
                </div>
              </SummaryGrid>
              <ModalText>
                {t('salesOverview.thisOrderHasBeenCreated')}
              </ModalText>
              <FormTextarea
                placeholder={t('salesOverview.eGCustomerIsThinking')}
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
              />
              <Btn
                className="danger"
                onClick={handleSubmitReason}
                disabled={!reasonText.trim() || submittingReason}
                style={{ width: '100%', marginTop: '0.85rem' }}
              >
                {submittingReason ? t('salesOverview.submitting') : t('salesOverview.submitReason')}
              </Btn>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* ── Header ──────────────────────────────────────────────────── */}
        <Header>
          <HeaderLeft>
            <Avatar>{initialsFor(userName)}</Avatar>
            <Greeting>
              <GreetingEyebrow>{t(greetingKeyFor())},</GreetingEyebrow>
              <TitleEl>{userName}</TitleEl>
            </Greeting>
          </HeaderLeft>
          <HeaderRight>
            <DatePill>
              <Calendar size={14} />
              {todayLabel}
            </DatePill>
          </HeaderRight>
        </Header>

        {/* ── Today's orders ──────────────────────────────────────────── */}
        <Section>
          <SectionHead>
            <SectionTitle $accent={tokens.brand}>
              <Receipt size={18} />
              {t('salesOverview.todaysOrders')}
              <Count>{counts.today}</Count>
            </SectionTitle>
            <SectionHint>{t('salesOverview.todaysHint')}</SectionHint>
          </SectionHead>

          <RowList>
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : todaysOrders.length === 0 ? (
              <Empty>
                <div className="icon-wrap"><Inbox size={22} /></div>
                <h4>{t('salesOverview.noOrdersToday')}</h4>
                <p>{t('salesOverview.noOrdersTodayHint')}</p>
              </Empty>
            ) : (
              todaysOrders.map(order => {
                const lp = lifecyclePill(order);
                const pp = paymentPill(order);
                return (
                  <Row
                    key={order._id}
                    $stripe={stripeFor(order)}
                    $highlight={isHighlighted(order)}
                    data-order-ref={order.orderNumber || order._id}
                  >
                    <RowMain>
                      <RowTopLine>
                        <OrderHash>{order.orderNumber}</OrderHash>
                        <CustomerName>{fullName(order.visitor, order)}</CustomerName>
                        <Pill $bg={lp.bg} $fg={lp.fg}>{lp.label}</Pill>
                        <Pill $bg={pp.bg} $fg={pp.fg}><CreditCard /> {pp.label}</Pill>
                      </RowTopLine>
                      <RowMeta>
                        <span><Clock size={12} />{new Date(order.orderDate || order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <span><strong>{fmtMoney(order.total)}</strong></span>
                        <span>{t('common.itemCount', { count: (order.items || []).length })}</span>
                      </RowMeta>
                    </RowMain>
                    <RowActions>
                      <Btn
                        className="secondary"
                        onClick={() => navigate(`/order/${order.orderNumber}`)}
                      >
                        {t('salesOverview.open')}
                      </Btn>
                    </RowActions>
                  </Row>
                );
              })
            )}
          </RowList>
        </Section>

        {/* ── Unpaid orders ───────────────────────────────────────────── */}
        <Section>
          <SectionHead>
            <SectionTitle $accent={tokens.warning}>
              <Coins size={18} />
              {t('salesOverview.unpaidOrders')}
              <Count $bg={tokens.warningSoft} $fg={tokens.warningDeep}>{counts.unpaid}</Count>
            </SectionTitle>
            <SectionHint>{t('salesOverview.unpaidHint')}</SectionHint>
          </SectionHead>

          <RowList>
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : unpaidOrders.length === 0 ? (
              <Empty>
                <div className="icon-wrap"><CheckCircle size={22} /></div>
                <h4>{t('salesOverview.settled')}</h4>
                <p>{t('salesOverview.settledHint')}</p>
              </Empty>
            ) : (
              unpaidOrders.map(order => {
                const balance = balanceOf(order);
                const phone = order.visitor?.phone;
                const ageDays = daysSince(order.orderDate || order.createdAt);
                return (
                  <Row
                    key={order._id}
                    $stripe={tokens.warning}
                    $highlight={isHighlighted(order)}
                    data-order-ref={order.orderNumber || order._id}
                  >
                    <RowMain>
                      <RowTopLine>
                        <OrderHash>{order.orderNumber}</OrderHash>
                        <CustomerName>{fullName(order.visitor, order)}</CustomerName>
                        <Pill $bg={tokens.warningSoft} $fg={tokens.warningDeep}>
                          {order.payment?.status === 'partial' ? t('salesOverview.partial') : t('salesOverview.unpaid')}
                        </Pill>
                      </RowTopLine>
                      <RowMeta>
                        <span>{t('salesOverview.total')} <strong>{fmtMoney(order.total)}</strong></span>
                        <span>{t('salesOverview.paid')} <strong>{fmtMoney(order.payment?.amount || 0)}</strong></span>
                        <span style={{ color: tokens.dangerDeep }}>
                          {t('salesOverview.owed')} <strong style={{ color: tokens.dangerDeep }}>{fmtMoney(balance)}</strong>
                        </span>
                        {ageDays !== null && <span><Clock size={12} />{ageDays === 0 ? t('common.today') : t('common.daysAgo', { count: ageDays })}</span>}
                      </RowMeta>
                    </RowMain>
                    <RowActions>
                      {phone ? (
                        <PhoneLink href={`tel:${phone}`} title={`Call ${phone}`}>
                          <PhoneCall size={14} /> {phone}
                        </PhoneLink>
                      ) : (
                        <PhoneEmpty><Phone size={14} /> {t('salesOverview.noPhone')}</PhoneEmpty>
                      )}
                      {canCollect ? (
                        <>
                          <Btn className="warning" onClick={() => openPayment(order)}>
                            <CreditCard size={14} /> {t('salesOverview.recordPayment')}
                          </Btn>
                          <Btn
                            className="success"
                            onClick={() => openPayment(order, { full: true })}
                            title={`Mark fully paid (${fmtMoney(balance)})`}
                          >
                            <CheckCircle size={14} /> {t('salesOverview.markFullyPaid')}
                          </Btn>
                        </>
                      ) : (
                        <PhoneEmpty><CreditCard size={14} /> {t('tasks.payAtCashier')}</PhoneEmpty>
                      )}
                    </RowActions>
                  </Row>
                );
              })
            )}
          </RowList>
        </Section>

        {/* ── Pending delivery ────────────────────────────────────────── */}
        <Section>
          <SectionHead>
            <SectionTitle $accent={tokens.success}>
              <Truck size={18} />
              {t('salesOverview.pendingDelivery')}
              <Count $bg={tokens.successSoft} $fg={tokens.successDeep}>{counts.delivery}</Count>
            </SectionTitle>
            <SectionHint>{t('salesOverview.pendingHint')}</SectionHint>
          </SectionHead>

          <RowList>
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : pendingDelivery.length === 0 ? (
              <Empty>
                <div className="icon-wrap"><PackageCheck size={22} /></div>
                <h4>{t('salesOverview.noDeliveries')}</h4>
                <p>{t('salesOverview.noDeliveriesHint')}</p>
              </Empty>
            ) : (
              pendingDelivery.map(order => {
                const balance = balanceOf(order);
                const phone = order.visitor?.phone;
                const ageDays = daysSince(order.updatedAt || order.createdAt);
                const fullyPaid = balance <= 0;
                return (
                  <Row
                    key={order._id}
                    $stripe={tokens.success}
                    $highlight={isHighlighted(order)}
                    data-order-ref={order.orderNumber || order._id}
                  >
                    <RowMain>
                      <RowTopLine>
                        <OrderHash>{order.orderNumber}</OrderHash>
                        <CustomerName>{fullName(order.visitor, order)}</CustomerName>
                        <Pill $bg={tokens.successSoft} $fg={tokens.successDeep}>{t('salesOverview.ready')}</Pill>
                        {!fullyPaid && (
                          <Pill $bg={tokens.dangerSoft} $fg={tokens.dangerDeep}>
                            {t('salesOverview.owes')} {fmtMoney(balance)}
                          </Pill>
                        )}
                      </RowTopLine>
                      <RowMeta>
                        <span>{t('salesOverview.total')} <strong>{fmtMoney(order.total)}</strong></span>
                        <span>{t('salesOverview.paid')} <strong>{fmtMoney(order.payment?.amount || 0)}</strong></span>
                        {ageDays !== null && <span><Clock size={12} />{ageDays === 0 ? t('salesOverview.justArrived') : t('common.daysAtStore', { count: ageDays })}</span>}
                      </RowMeta>
                    </RowMain>
                    <RowActions>
                      {phone ? (
                        <PhoneLink href={`tel:${phone}`} title={`Call ${phone}`}>
                          <PhoneCall size={14} /> {phone}
                        </PhoneLink>
                      ) : (
                        <PhoneEmpty><Phone size={14} /> {t('salesOverview.noPhone')}</PhoneEmpty>
                      )}
                      {canDeliver && (
                        <Btn className="primary" onClick={() => openDeliver(order)}>
                          <PackageCheck size={14} /> {t('salesOverview.deliverToCustomer')}
                        </Btn>
                      )}
                    </RowActions>
                  </Row>
                );
              })
            )}
          </RowList>
        </Section>

        {/* ── Add Doctor modal ────────────────────────────────────────── */}
        {showDoctorModal && (
          <ModalOverlay onClick={() => { setShowDoctorModal(false); setDoctorSuccess(''); }}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHead>
                <ModalTitleEl>
                  <span className="icon-wrap"><UserPlus size={20} /></span>
                  {t('salesOverview.addNewDoctor')}
                </ModalTitleEl>
                <ModalCloseBtn onClick={() => { setShowDoctorModal(false); setDoctorSuccess(''); }}>
                  <X size={18} />
                </ModalCloseBtn>
              </ModalHead>

              {doctorSuccess && (
                <SuccessBanner>
                  <CheckCircle size={14} />
                  {doctorSuccess}
                </SuccessBanner>
              )}

              <FormGroup>
                <FormLabel><User size={14} /> {t('salesOverview.name')}</FormLabel>
                <FormInput
                  type="text"
                  placeholder={t('salesOverview.drDoctorSFullName')}
                  value={doctorForm.name}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-Z\s'.-]/g, '');
                    setDoctorForm(prev => ({ ...prev, name: value }));
                  }}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel><Phone size={14} /> {t('salesOverview.phoneNumber')}</FormLabel>
                <FormInput
                  type="tel"
                  placeholder={t('salesOverview.optional')}
                  value={doctorForm.phoneNumber}
                  onChange={(e) => setDoctorForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel><MapPin size={14} /> {t('salesOverview.address')}</FormLabel>
                <FormInput
                  type="text"
                  placeholder={t('salesOverview.optional')}
                  value={doctorForm.address}
                  onChange={(e) => setDoctorForm(prev => ({ ...prev, address: e.target.value }))}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel><MapPin size={14} /> {t('salesOverview.city')}</FormLabel>
                <FormInput
                  type="text"
                  placeholder={t('salesOverview.optional')}
                  value={doctorForm.city}
                  onChange={(e) => setDoctorForm(prev => ({ ...prev, city: e.target.value }))}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel><MapPin size={14} /> {t('salesOverview.country')}</FormLabel>
                <FormInput
                  type="text"
                  placeholder={t('salesOverview.optional')}
                  value={doctorForm.country}
                  onChange={(e) => setDoctorForm(prev => ({ ...prev, country: e.target.value }))}
                />
              </FormGroup>

              <ModalActions>
                <Btn
                  className="secondary"
                  onClick={() => { setShowDoctorModal(false); setDoctorSuccess(''); }}
                >
                  {t('salesOverview.cancel')}
                </Btn>
                <Btn
                  className="primary"
                  onClick={handleSaveDoctor}
                  disabled={!doctorForm.name.trim() || doctorSaving}
                >
                  {doctorSaving ? t('salesOverview.saving') : t('salesOverview.saveDoctor')}
                </Btn>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* ── Payment modal ───────────────────────────────────────────── */}
        {paymentModal.open && paymentModal.order && (
          <ModalOverlay onClick={closePayment}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHead>
                <ModalTitleEl $tone="warning">
                  <span className="icon-wrap"><Coins size={20} /></span>
                  {t('salesOverview.recordPayment')}
                </ModalTitleEl>
                <ModalCloseBtn onClick={closePayment}><X size={18} /></ModalCloseBtn>
              </ModalHead>

              <ModalText>
                {t('salesOverview.recordingAPaymentFor')} <strong>{fullName(paymentModal.order.visitor, paymentModal.order)}</strong> {t('salesOverview.order')}{paymentModal.order.orderNumber}.
              </ModalText>

              <SummaryGrid>
                <div><span className="label">{t('salesOverview.total')}</span><span className="value">{fmtMoney(paymentModal.order.total)}</span></div>
                <div><span className="label">{t('salesOverview.paidSoFar')}</span><span className="value">{fmtMoney(paymentModal.order.payment?.amount || 0)}</span></div>
                <div><span className="label">{t('salesOverview.balanceOwed')}</span><span className="value" style={{ color: tokens.dangerDeep }}>{fmtMoney(balanceOf(paymentModal.order))}</span></div>
                <div><span className="label">{t('salesOverview.status')}</span><span className="value">{(paymentModal.order.payment?.status || 'pending').toUpperCase()}</span></div>
              </SummaryGrid>

              {paymentError && <InlineError>{paymentError}</InlineError>}

              <FormGroup>
                <FormLabel><CreditCard size={14} /> {t('salesOverview.amountDzd')}</FormLabel>
                <FormInput
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>{t('salesOverview.method')}</FormLabel>
                <FormSelect
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, method: e.target.value }))}
                >
                  <option value="cash">{t('salesOverview.cash')}</option>
                  <option value="credit_card">{t('salesOverview.creditCard')}</option>
                  <option value="debit_card">{t('salesOverview.debitCard')}</option>
                  <option value="check">{t('salesOverview.check')}</option>
                  <option value="insurance">{t('salesOverview.insurance')}</option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel>{t('salesOverview.notes')}</FormLabel>
                <FormTextarea
                  placeholder={t('salesOverview.optionalReferenceReceiptEtc')}
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                />
              </FormGroup>

              <ModalActions>
                <Btn className="secondary" onClick={closePayment} disabled={paymentSaving}>{t('salesOverview.cancel')}</Btn>
                <Btn className="primary" onClick={submitPayment} disabled={paymentSaving}>
                  {paymentSaving ? t('salesOverview.saving') : t('salesOverview.savePayment')}
                </Btn>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* ── Delivery modal ──────────────────────────────────────────── */}
        {deliverModal.open && deliverModal.order && (() => {
          const order = deliverModal.order;
          const balance = balanceOf(order);
          const paid = paidOf(order);
          const minimumPaid = minimumPaidForDelivery(order);
          const needsWaiver = requiresDeliveryWaiver(order);
          return (
            <ModalOverlay onClick={closeDeliver}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHead>
                  <ModalTitleEl $tone="success">
                    <span className="icon-wrap"><PackageCheck size={20} /></span>
                    {t('salesOverview.deliverToCustomer')}
                  </ModalTitleEl>
                  <ModalCloseBtn onClick={closeDeliver}><X size={18} /></ModalCloseBtn>
                </ModalHead>

                <ModalText>
                  {t('salesOverview.handingOverTo')} <strong>{fullName(order.visitor, order)}</strong> {t('salesOverview.order')}{order.orderNumber}.
                </ModalText>

                <SummaryGrid>
                  <div><span className="label">{t('salesOverview.total')}</span><span className="value">{fmtMoney(order.total)}</span></div>
                  <div><span className="label">{t('salesOverview.paid')}</span><span className="value">{fmtMoney(paid)}</span></div>
                  <div>
                    <span className="label">{t('salesOverview.balance')}</span>
                    <span className="value" style={{ color: balance > 0 ? tokens.dangerDeep : tokens.successDeep }}>
                      {fmtMoney(balance)}
                    </span>
                  </div>
                  <div>
                    <span className="label">{t('salesOverview.x50Minimum')}</span>
                    <span className="value" style={{ color: needsWaiver ? tokens.warningDeep : tokens.successDeep }}>
                      {fmtMoney(minimumPaid)}
                    </span>
                  </div>
                  <div>
                    <span className="label">{t('salesOverview.afterDelivery')}</span>
                    <span className="value">{t('salesOverview.customerSupportSurvey')}</span>
                  </div>
                </SummaryGrid>

                {deliverError && <InlineError>{deliverError}</InlineError>}

                {needsWaiver && !canCollect && (
                  <ModalText>{t('tasks.sendToCashierFirst')}</ModalText>
                )}

                {needsWaiver && (
                  <WaiverBlock>
                    <input
                      type="checkbox"
                      checked={deliverForm.waiverSigned}
                      onChange={(e) => setDeliverForm(prev => ({ ...prev, waiverSigned: e.target.checked }))}
                    />
                    <span>
                      <ShieldCheck size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      <strong>{t('salesOverview.waiver')}</strong> {t('salesOverview.paymentIsUnder50The')} <strong>{fmtMoney(balance)}</strong> {t('salesOverview.andCommitsToSettleIt')}
                    </span>
                  </WaiverBlock>
                )}

                <FormGroup>
                  <FormLabel>{t('salesOverview.notesOptional')}</FormLabel>
                  <FormTextarea
                    placeholder={t('salesOverview.eGDeliveredToSpouse')}
                    value={deliverForm.notes}
                    onChange={(e) => setDeliverForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </FormGroup>

                <ModalActions>
                  <Btn className="secondary" onClick={closeDeliver} disabled={deliverSaving}>{t('salesOverview.cancel')}</Btn>
                  <Btn
                    className="success"
                    onClick={submitDeliver}
                    disabled={deliverSaving || (needsWaiver && !deliverForm.waiverSigned)}
                  >
                    {deliverSaving ? t('salesOverview.delivering') : t('salesOverview.confirmDelivery')}
                  </Btn>
                </ModalActions>
              </ModalContent>
            </ModalOverlay>
          );
        })()}

      </PageWrap>
    </SalesLayout>
  );
};

export default SalesOverview;
